import React, { useState, FC } from "react";
import SearchBar from "../molecules/SearchBar";
import ResponsiveTable from "components/organisms/ResponsiveTable";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link } from "react-router-dom";
import { faLongArrowAltLeft } from "@fortawesome/free-solid-svg-icons";
import { AuditEvent } from "types/track/AuditEventType";
import { useTrackPageEffect } from "hooks/services/useTrackPageEffect";
import {Stack, Typography } from "@mui/material";
import SelectedClaimDetail from "components/organisms/SelectedClaimDetail";
import { useClaims } from "hooks/useClaims";
import { search, yyyyddmmTommddyyyy } from "common/utility";
import { ExtendedMemberClaim } from "types/ExtendedMemberClaim";

export const Claims: FC = (props) => {
  const [searchValue, setSearchValue] = useState(null);
  const [selectedClaim, setSelectedClaim] = useState<ExtendedMemberClaim | null>();

  useTrackPageEffect({
    eventType: AuditEvent.MemberPortalViewClaims,
  });

  const { data: claims, isLoading, isFetched } = useClaims();

  return (
    <Stack my={2}>
      {selectedClaim ? (
        <>
          <Stack
            direction="row"
            my={2}
            spacing={2}
            alignItems="center"
            onClick={() => setSelectedClaim(null)}
          >
            <FontAwesomeIcon icon={faLongArrowAltLeft} aria-label="Go Back" />
            <Typography component="span">Back</Typography>
          </Stack>
          <SelectedClaimDetail claim={selectedClaim} />
        </>
      ) : (
        <Stack spacing={2}>
          <Typography variant="h2" component="h1" fontWeight="bold">
            Recent Claims
          </Typography>
          <Typography variant="body1" mb={2} component="div">
            Claims displayed below reflect services rendered the previous 12
            months, up to the 50 most recent claims. For more information about
            claims that are older than 12 months, or claims not displayed here,{" "}
            <Link to="/contact-us">contact us</Link>.{" "}
          </Typography>
          <SearchBar
            placeholder="Search Claims by number, type, service provider"
            onChange={setSearchValue}
          />
          <ResponsiveTable<ExtendedMemberClaim>
            isFirstTimeLoading={isLoading && !isFetched}
            tableOptions={{
              columns: [
                {
                  accessorKey: "claim_number",
                  header: "Claim Number",
                  meta: {
                    width: "33%",
                  },
                },
                {
                  accessorKey: "provider_name",
                  header: "Service Provider",
                  meta: {
                    width: "20%",
                  },
                },
                {
                  accessorKey: "service_date",
                  header: "Service Date",
                },
                { accessorKey: "claim_type", header: "Claim Type" },
                {
                  accessorKey:
                    "claim_detail.total_patient_responsibility_amount",
                  header: "Your Responsibility",
                  cell: ({ row }: { row: { original: ExtendedMemberClaim } }) =>
                    row.original.claim_detail
                      ? `$${row.original.claim_detail.total_patient_responsibility_amount.toFixed(2)}`
                      : "$0.00",
                },
              ],
              data:
                (search(claims || [], searchValue) as ExtendedMemberClaim[]).map((claim) => ({
                  ...claim,
                  service_date: claim.claim_detail
                    ? `${yyyyddmmTommddyyyy(
                        claim.claim_detail.service_from_date
                      )} - ${yyyyddmmTommddyyyy(
                        claim.claim_detail.service_to_date
                      )}`
                    : "",
                })) || [],
            }}
            onClickRow={setSelectedClaim}
          />
        </Stack>
      )}
    </Stack>
  );
};

export default Claims;
